export default function Footer() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="relative bg-gradient-to-br from-[#0A1628] via-[#1A2B3C] to-[#0F1F2E] overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/[0.02] rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/[0.01] rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-12 py-20">
        {/* Top section with logo and CTA */}
        <div className="flex items-start justify-between mb-20 pb-16 border-b border-white/10">
          <div className="max-w-[480px]">
            <h3
              className="text-[42px] font-semibold text-white leading-tight mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              理想の住まいを
              <br />
              一緒に見つけましょう
            </h3>
            <p
              className="text-[15px] text-white/60 leading-relaxed mb-8"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              お客様の大切な資産選びを、経験豊富な専門スタッフが
              <br />
              丁寧にサポートいたします。まずはお気軽にご相談ください。
            </p>
            <button
              className="group flex items-center gap-3 h-[54px] px-8 bg-white text-[#0A1628] rounded-full text-[14px] font-medium hover:bg-white/90 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              <span>無料相談を予約する</span>
              <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>

          {/* Newsletter subscription */}
          <div className="w-[420px]">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <i className="ri-mail-line text-white text-lg"></i>
                </div>
                <h5
                  className="text-[16px] font-medium text-white"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  最新情報をお届け
                </h5>
              </div>
              <p
                className="text-[13px] text-white/60 mb-6 leading-relaxed"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                新着物件情報や不動産市場のトレンドなど、
                <br />
                お役立ち情報を定期的にお送りします
              </p>
              <div className="relative">
                <input
                  type="email"
                  placeholder="メールアドレスを入力"
                  className="w-full h-[52px] px-5 pr-14 bg-white/10 border border-white/20 rounded-xl text-white text-[14px] placeholder:text-white/40 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
                <button className="absolute right-1.5 top-1.5 w-11 h-11 bg-white rounded-lg flex items-center justify-center hover:bg-white/90 transition-colors cursor-pointer">
                  <i className="ri-send-plane-fill text-[#0A1628] text-base"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main footer content */}
        <div className="grid grid-cols-5 gap-12">
          {/* Company info */}
          <div className="col-span-2">
            <div
              className="text-[32px] font-bold text-white mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              プレミアム不動産
            </div>
            <div className="space-y-3 text-white/60 text-[14px] mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-white/40 text-base mt-0.5"></i>
                <div>
                  <p>〒100-0001</p>
                  <p>東京都千代田区千代田1-1-1</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-phone-line text-white/40 text-base"></i>
                <p>TEL: 03-1234-5678</p>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-time-line text-white/40 text-base"></i>
                <p>営業時間: 9:00-18:00</p>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-calendar-line text-white/40 text-base"></i>
                <p>定休日: 水曜日・祝日</p>
              </div>
            </div>

            {/* Social media */}
            <div className="flex items-center gap-3">
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-white text-base"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-youtube-line text-white text-base"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-box-line text-white text-base"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line text-white text-base"></i>
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h5
              className="text-[15px] font-semibold text-white mb-6"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              サイトマップ
            </h5>
            <nav className="space-y-4">
              <button
                onClick={() => scrollToSection('about')}
                className="block text-[14px] text-white/60 hover:text-white transition-colors cursor-pointer"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                会社概要
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="block text-[14px] text-white/60 hover:text-white transition-colors cursor-pointer"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                サービス
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="block text-[14px] text-white/60 hover:text-white transition-colors cursor-pointer"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                お客様の声
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block text-[14px] text-white/60 hover:text-white transition-colors cursor-pointer"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                お問い合わせ
              </button>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h5
              className="text-[15px] font-semibold text-white mb-6"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              サービス
            </h5>
            <nav className="space-y-4">
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                物件売買
              </a>
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                賃貸仲介
              </a>
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                資産運用相談
              </a>
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                リフォーム
              </a>
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h5
              className="text-[15px] font-semibold text-white mb-6"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              法的情報
            </h5>
            <nav className="space-y-4">
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                プライバシーポリシー
              </a>
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                利用規約
              </a>
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                特定商取引法
              </a>
              <a
                href="#"
                className="block text-[14px] text-white/60 hover:text-white transition-colors"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                免責事項
              </a>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
